import type { SectorValueStatus } from './types';
import { CANONICAL_GHG_CONTRACT } from './contract';

export const SOLID_WASTE_FACTOR_CAP_YEAR =
  CANONICAL_GHG_CONTRACT.methodVersions.solid_waste_factor_cap_year;

export type ZeroWasteRootCauseClass =
  | 'missing_county_factor'
  | 'failed_geography_crosswalk'
  | 'malformed_geofips'
  | 'missing_landfill_destination'
  | 'failed_join'
  | 'null_to_zero_conversion'
  | 'incomplete_batch_generation'
  | 'stale_cache'
  | 'unsupported_geography'
  | 'valid_scientific_zero'
  | 'other_pipeline_defect';

export interface SolidWasteYearResult {
  year: number;
  wasteTons: number;
  emissionFactor: number | null;
  factorVintageYear: number | null;
  emissionsMt: number | null;
  status: SectorValueStatus;
  rootCause?: ZeroWasteRootCauseClass;
  notes?: string;
}

/** Resolve emissions for one year — never coerce missing factor to zero when tons are positive. */
export function resolveSolidWasteYearValue(
  year: number,
  wasteTons: number,
  emissionFactor: number | null | undefined,
  factorVintageYear: number | null = SOLID_WASTE_FACTOR_CAP_YEAR
): SolidWasteYearResult {
  const tons = Math.max(0, Number(wasteTons) || 0);
  const factor =
    emissionFactor != null && Number.isFinite(emissionFactor) && emissionFactor > 0
      ? emissionFactor
      : null;

  if (tons === 0) {
    return {
      year,
      wasteTons: 0,
      emissionFactor: factor,
      factorVintageYear,
      emissionsMt: 0,
      status: 'not_applicable',
      notes: 'No waste activity reported',
    };
  }

  if (factor == null) {
    return {
      year,
      wasteTons: tons,
      emissionFactor: null,
      factorVintageYear,
      emissionsMt: null,
      status: 'unavailable',
      rootCause: 'missing_county_factor',
      notes: `Nonzero waste tonnage (${tons}) with no county landfill emission factor`,
    };
  }

  const emissionsMt = Math.round(tons * factor);
  return {
    year,
    wasteTons: tons,
    emissionFactor: factor,
    factorVintageYear,
    emissionsMt,
    status: 'provisional',
    notes: `Provisional county landfill factor; factor data capped at ${factorVintageYear}`,
  };
}

export function classifyZeroWasteRootCause(input: {
  wasteTons: number;
  emissionFactor: number | null;
  countyGeofipsResolved: boolean;
  wasteRowsFound: boolean;
  landfillRowsFound: boolean;
  geofipsVariantsTried: number;
}): ZeroWasteRootCauseClass {
  const { wasteTons, emissionFactor, countyGeofipsResolved, wasteRowsFound, landfillRowsFound } =
    input;

  if (wasteTons > 0 && emissionFactor != null && emissionFactor > 0) {
    return 'other_pipeline_defect';
  }
  if (wasteTons === 0) return 'valid_scientific_zero';
  if (!wasteRowsFound && wasteTons > 0) return 'failed_join';
  if (!countyGeofipsResolved) return 'failed_geography_crosswalk';
  if (!landfillRowsFound) return 'missing_landfill_destination';
  if (wasteTons > 0 && (emissionFactor == null || emissionFactor === 0)) {
    return 'missing_county_factor';
  }
  return 'other_pipeline_defect';
}

/** Chart-safe value: null when unavailable, numeric otherwise. */
export function solidWasteChartValue(result: SolidWasteYearResult): number | null {
  if (result.status === 'unavailable') return null;
  return result.emissionsMt ?? 0;
}
